import {
  clearAllBodyScrollLocks,
  disableBodyScroll,
  enableBodyScroll,
} from 'body-scroll-lock';
import React, {
  useEffect,
  useContext,
  useState,
  SetStateAction,
  FC,
} from 'react';
import styled from 'styled-components';

import BlankProsperCard from '../../assets/icons/blank-cc-backside.svg';
import BulletCheckIconSVG from '../../assets/icons/bullet-check-icon.svg';
import UpCaret from '../../assets/icons/caret-up.svg';
import ExitSVG from '../../assets/icons/x-close.svg';
import { CreditCardApplicationContext } from '../../CreditCardApplicationContext';
import {
  CARD_BREAKPOINT,
  colors,
  MOBILE_BREAKPOINT,
  MOBILE_WIDTH,
  TABLET_BREAKPOINT,
} from '../constants';
import {
  formatNumberWithCommas,
  formatPossessive,
} from '../utilities/formatting';

import { CreditCardNameGenerator } from './CreditCardNameGenerator';
import { ModalWrapper } from './ModalWrapper';

interface OfferGlanceProps {
  conditionalOnCashflowVerification: boolean;
  active: boolean;
  setActive: React.Dispatch<SetStateAction<boolean>>;
}

interface VisibilityProps {
  visible: boolean;
}

// #region -- Styles -----------------------------------------------------------------------------
// Button in ApplicationRouteNav opens Drawer when clicked
const NavButton = styled.button<VisibilityProps>`
  position: absolute;
  right: 2rem;
  height: 2.25rem;
  border-radius: 1.5rem;
  display: flex;
  justify-content: space-around;
  align-items: center;
  background-color: ${colors.UI_SECONDARY};
  cursor: pointer;
  visibility: ${(p: VisibilityProps) => (p.visible ? 'visible' : 'hidden')};
  @media (max-width: ${TABLET_BREAKPOINT}) {
    top: 1rem;
    margin: 0 0 2.125rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    right: 1rem;
  }
`;
NavButton.displayName = 'NavButton';

const NavButtonCardIcon = styled(BlankProsperCard)`
  margin: 0 0 0 0.9375rem;
`;
NavButtonCardIcon.displayName = 'NavButtonCardIcon';

const NavButtonText = styled.p`
  margin: 0 0.4375rem;
  font-size: 0.75rem;
  color: ${colors.TEXT_PRIMARY};
`;
NavButtonText.displayName = 'NavButtonText';

const NavButtonCaretContainer = styled.div<VisibilityProps>`
  margin: 0 0.875rem 0 0;
  display: flex;
  transform: ${(p: VisibilityProps) =>
    p.visible ? 'rotate(0)' : 'rotate(180deg)'};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
NavButtonCaretContainer.displayName = 'NavButtonCaretContainer';

const NavButtonCaret = styled(UpCaret)`
  path {
    stroke: ${colors.TEXT_PRIMARY};
  }
`;
NavButtonCaret.displayName = 'NavButtonCaret';

// Click-outside-to-close gray area is visible behind an open drawer
const DimmedBackground = styled.div<VisibilityProps>`
  z-index: 1;
  position: fixed;
  top: 5.5625rem;
  left: 0;
  height: 100vh;
  width: 100%;
  display: none;
  ${(p: VisibilityProps) =>
    p.visible &&
    `
    display: block;
    background-color: #333333;
    opacity: 0.25;
  `};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
DimmedBackground.displayName = 'DimmedBackground';

// Container for all the drawer elements - preview card and features list
const Drawer = styled.div<VisibilityProps>`
  z-index: 2;
  position: absolute;
  top: 5.5625rem;
  left: 0;
  height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${colors.DROPDOWN_BACKGROUND};
  overflow: hidden;
  transition: all ease-in-out 0.25s;
  transition-property: height;
  ${(p: VisibilityProps) =>
    p.visible &&
    `
    height: 15.125rem;
    @media (max-width: ${CARD_BREAKPOINT}) {
      height: 25.4375rem;
    }
  `};
  @media (max-width: ${TABLET_BREAKPOINT}) {
    top: 4.3125rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: none;
  }
`;
Drawer.displayName = 'Drawer';

// Container breaks elements inside the drawer into a column for slim screens
const DrawerContent = styled.div`
  margin: 0.5625rem 2rem 0 6rem;
  display: flex;
  align-self: center;
  flex-direction: row;
  justify-content: center;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    margin: 0.5625rem 1.6875rem 0;
  }
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0.875rem 1.6875rem 0;
    flex-direction: column;
    align-self: flex-start;
  }
`;
DrawerContent.displayName = 'DrawerContent';

// Container for the preview card image within the drawer
const OfferCardPreview = styled.div`
  padding: 0.625rem 0 0 1.625rem;
  align-self: flex-start;
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 0;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-bottom: 1.375rem;
  }
`;
OfferCardPreview.displayName = 'OfferCardPreview';

// Container for the perks of the Prosper Card
const OfferFeatures = styled.div`
  margin: 0 0 0 2.1875rem;
  width: 21.875rem;
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0.75rem 0 0 0;
    width: 95%;
  }
`;
OfferFeatures.displayName = 'OfferFeatures';

const FeatureListTitle = styled.p`
  padding: 0.875rem 0 0.5625rem;
  font-weight: 500;
  color: ${colors.TEXT_PRIMARY};
  @media (max-width: ${CARD_BREAKPOINT}) {
    padding: 0.75rem 0 0.6875rem;
  }
`;
FeatureListTitle.displayName = 'FeatureListTitle';

const FeatureList = styled.ul``;
FeatureList.displayName = 'FeatureList';

const FeatureListItem = styled.li`
  padding: 0.125rem 0;
  display: grid;
  grid-template-columns: 1.25rem 1fr;
  font-size: 0.875rem;
`;
FeatureListItem.displayName = 'FeatureListItem';

const ListItemBullet = styled(BulletCheckIconSVG)`
  height: 0.6875rem;
  width: 0.6875rem;
  margin: 0.25rem 0.5rem 0 0;
`;
ListItemBullet.displayName = 'ListItemBullet';

const ListItemText = styled.p`
  line-height: 1.25rem;
  color: ${colors.TEXT_LABEL};
`;
ListItemText.displayName = 'ListItemText';

const OfferGlanceModal = styled.div`
  text-align: left;
  background-color: ${colors.DROPDOWN_BACKGROUND};
  z-index: 3;
  padding: 0 0 2rem 1.5rem;
`;
OfferGlanceModal.displayName = 'OfferGlanceModal';

// X button to close the Offer Glance modal on mobile devices
const ExitButton = styled(ExitSVG)`
  position: absolute;
  right: 0;
  top: 0;
  display: inline;
`;
ExitButton.displayName = 'ExitButton';

// Button at the bottom of the drawer closes the drawer when clicked
const HideDrawerButton = styled.button<VisibilityProps>`
  margin: 0.8125rem auto 1rem;
  padding: 0 1.375rem 0 0;
  display: block;
  font-size: 0.75rem;
  line-height: 1.3125rem;
  cursor: pointer;
  color: ${colors.TEXT_LABEL};
  ${(p: VisibilityProps) =>
    !p.visible &&
    `
    display: none;
    cursor: default;
    pointer-events: none;
  `};
  @media (max-width: ${CARD_BREAKPOINT}) {
    margin: 0 auto 0.75rem;
  }
`;
HideDrawerButton.displayName = 'HideDrawerButton';

const HideDrawerCaret = styled(UpCaret)`
  margin: 0.0625rem 0.25rem;
  path {
    stroke: ${colors.TEXT_LABEL};
  }
`;
HideDrawerCaret.displayName = 'HideDrawerCaret';
// #endregion

// Modal version of the drawer shown on mobile screen widths
const OfferGlanceModalRender = (
  props: OfferGlanceProps,
  creditLimit: string,
  width?: number,
  firstName?: string,
  lastName?: string
) => {
  const { conditionalOnCashflowVerification, active, setActive } = props;
  const featureListText = conditionalOnCashflowVerification
    ? 'Your Pre-Approved* Card Offer'
    : 'Your Prosper<sup>®</sup> Card Offer';

  return (
    <ModalWrapper
      id="offer-glance-modal"
      showModal={active && width <= MOBILE_WIDTH}
      setShowModal={setActive}
      marginBottom="0"
    >
      <OfferGlanceModal>
        <OfferFeatures>
          <OfferCardPreview>
            <CreditCardNameGenerator
              firstName={firstName}
              lastName={lastName}
              offerPreview
            />
          </OfferCardPreview>
          <FeatureListTitle
            dangerouslySetInnerHTML={{ __html: featureListText }}
          />
          <FeatureList>
            <FeatureListItem>
              <ListItemBullet aria-hidden />
              <ListItemText>
                $
                {creditLimit &&
                  creditLimit !== '' &&
                  formatNumberWithCommas(creditLimit)}{' '}
                credit limit after your card arrives
              </ListItemText>
            </FeatureListItem>
            <FeatureListItem>
              <ListItemBullet aria-hidden />
              <ListItemText>No security deposit required</ListItemText>
            </FeatureListItem>
            <FeatureListItem>
              <ListItemBullet aria-hidden />
              <ListItemText>
                $0 first year annual fee with AutoPay²
              </ListItemText>
            </FeatureListItem>
            <FeatureListItem>
              <ListItemBullet aria-hidden />
              <ListItemText>
                You’ll be reviewed for credit line increases¹
              </ListItemText>
            </FeatureListItem>
            <FeatureListItem>
              <ListItemBullet aria-hidden />
              <ListItemText>
                Instantly access 50% of your credit during the 7–10 business day
                shipping period
              </ListItemText>
            </FeatureListItem>
          </FeatureList>
        </OfferFeatures>
      </OfferGlanceModal>
    </ModalWrapper>
  );
};
// Component --------------------------------------------------------------------------------------
export const OfferGlance: FC<OfferGlanceProps> = ({
  conditionalOnCashflowVerification,
  active,
  setActive,
}) => {
  const [width, setWidth] = useState(window.innerWidth);

  const useContextReturn = useContext(CreditCardApplicationContext);
  const { creditCardApplication } = useContextReturn;
  const { applicationId } = creditCardApplication || {};
  const { applicant, offerCheck } = creditCardApplication || {};
  const { offer } = offerCheck || {};

  const creditLimit = offer ? offer.creditLimit : null;
  const firstName = applicant ? applicant.firstName : '';
  const lastName = applicant ? applicant.lastName : '';
  const featureListText = conditionalOnCashflowVerification
    ? 'Your Pre-Approved* Card Offer'
    : 'Your Prosper<sup>®</sup> Card Offer';

  // Enables body scroll-lock while the drawer/modal is open
  useEffect(() => {
    if (active) {
      disableBodyScroll(document.body);
    } else {
      enableBodyScroll(document.body);
    }
    return () => {
      clearAllBodyScrollLocks();
    };
  }, [active]);

  // Determines if the mobile modal should be shown instead of the drawer
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });

  // Render ----------------------------------------------------------------------------------------
  return (
    <>
      <NavButton
        id="offer-glance"
        type="button"
        onClick={() => setActive(!active)}
        aria-label="View offer at a glance"
        visible={typeof applicationId === 'string' && applicationId.length > 0}
        aria-expanded={active}
        aria-controls="offer-glance-drawer"
      >
        <NavButtonCardIcon id="blank-card" />
        <NavButtonText id="client-card">
          {firstName && formatPossessive(firstName).length < 12
            ? `${formatPossessive(firstName)} Card`
            : 'Your Card'}
        </NavButtonText>
        <NavButtonCaretContainer visible={active}>
          <NavButtonCaret id="offer-glance-caret" />
        </NavButtonCaretContainer>
      </NavButton>
      <DimmedBackground
        visible={active || undefined}
        onClick={() => setActive(false)}
      />
      <Drawer id="offer-glance-drawer" visible={active || undefined}>
        <DrawerContent>
          <OfferCardPreview>
            <CreditCardNameGenerator
              firstName={firstName}
              lastName={lastName}
              offerPreview
            />
          </OfferCardPreview>
          <OfferFeatures>
            <FeatureListTitle
              dangerouslySetInnerHTML={{ __html: featureListText }}
            />
            <FeatureList>
              <FeatureListItem>
                <ListItemBullet aria-hidden />
                <ListItemText>
                  $
                  {creditLimit &&
                    creditLimit !== '' &&
                    formatNumberWithCommas(creditLimit)}{' '}
                  credit limit after your card arrives
                </ListItemText>
              </FeatureListItem>
              <FeatureListItem>
                <ListItemBullet aria-hidden />
                <ListItemText>No security deposit required</ListItemText>
              </FeatureListItem>
              <FeatureListItem>
                <ListItemBullet aria-hidden />
                <ListItemText>
                  $0 first year annual fee with AutoPay²
                </ListItemText>
              </FeatureListItem>
              <FeatureListItem>
                <ListItemBullet aria-hidden />
                <ListItemText>
                  You’ll be reviewed for credit line increases¹
                </ListItemText>
              </FeatureListItem>
              <FeatureListItem>
                <ListItemBullet aria-hidden />
                <ListItemText>
                  Instantly access 50% of your credit during the 7–10 business
                  day shipping period
                </ListItemText>
              </FeatureListItem>
            </FeatureList>
          </OfferFeatures>
        </DrawerContent>
        <HideDrawerButton
          id="offer-glance-hide-button"
          type="button"
          onClick={() => setActive(false)}
          aria-label="Hide offer at a glance"
          visible={active}
          aria-expanded={active}
          aria-controls="offer-glance-drawer"
        >
          <HideDrawerCaret
            viewBox="0 0 10 5"
            width="14"
            height="7"
            strokeWidth="0.75"
          />
          Hide
        </HideDrawerButton>
      </Drawer>
      {OfferGlanceModalRender(
        { conditionalOnCashflowVerification, active, setActive },
        creditLimit,
        width,
        firstName,
        lastName
      )}
    </>
  );
};
